import { useState } from "react";
import { Modal } from "../ui/Overlay";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Notice";
import { I18nGrid } from "./I18nGrid";
import { BASE_LOCALE, localesOf } from "../../lib/locales";
import type { Level } from "../../lib/model";
import { useT } from "../../lib/i18n";

/**
 * 关卡名称的多语言配置：城市名 + 主建筑两个字段，所有语言同屏对照。
 *
 * 英文是默认语言，直接存在 Level.name / Level.stage；其余语言进 i18n。
 * 这么分是因为**英文必填、其余可缺**——把英文也塞进 i18n 会让「默认值取哪个」
 * 变成每个调用点各判一次。
 */
export function LevelNamesDialog({
  lv, editable, onClose, onChange,
}: {
  lv: Level;
  editable: boolean;
  onClose: () => void;
  onChange: (patch: Partial<Level>) => void;
}) {
  const t = useT();
  const [locales, setLocales] = useState<string[]>(() => localesOf(
    Object.fromEntries(Object.entries(lv.i18n).map(([k, v]) => [k, v.name ?? ""])),
  ));

  const get = (id: string, field: string) =>
    id === BASE_LOCALE ? String(lv[field as "name" | "stage"] ?? "") : (lv.i18n[id]?.[field as "name" | "stage"] ?? "");

  const set = (id: string, field: string, v: string) => {
    if (id === BASE_LOCALE) return onChange({ [field]: v } as Partial<Level>);
    onChange({ i18n: { ...lv.i18n, [id]: { ...(lv.i18n[id] ?? {}), [field]: v } } });
  };

  const baseMissing = !String(lv.name ?? "").trim();

  return (
    <Modal
      title={t("dlg.names", { n: lv.id })}
      width={760}
      onClose={onClose}
      footer={<Button tone="primary" onClick={onClose}>{t("common.done")}</Button>}
    >
      <div className="flex flex-col gap-3">
        <Notice tone={baseMissing ? "danger" : "plain"}>{t("dlg.i18nNote")}</Notice>
        <I18nGrid
          locales={locales}
          editable={editable}
          fields={[
            { key: "name", label: t("dlg.cityName") },
          ]}
          value={get}
          onChange={set}
          onAddLocale={(id) => setLocales([...locales, id])}
          onRemoveLocale={(id) => {
            setLocales(locales.filter((x) => x !== id));
            const next = { ...lv.i18n };
            delete next[id];
            onChange({ i18n: next });
          }}
        />
      </div>
    </Modal>
  );
}
